// src/stats/api.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type {
  AppIntegration,
  AppReview,
  StatsSummary,
  StatsSync,
  StatsTarget,
} from '../lib/types'

// ── Clés de cache ──────────────────────────────────────────────────────────

const statsKeys = {
  summary: (appId: string, from: string, to: string) =>
    ['stats', 'summary', appId, from, to] as const,
  series: (appId: string, metric: string, from: string, to: string) =>
    ['stats', 'series', appId, metric, from, to] as const,
  reviews: (appId: string, limit: number) =>
    ['stats', 'reviews', appId, limit] as const,
  targets: (appId: string) => ['stats', 'targets', appId] as const,
  sync: (appId: string) => ['stats', 'sync', appId] as const,
  integrations: (appId: string) => ['stats', 'integrations', appId] as const,
}

// ── Queries ────────────────────────────────────────────────────────────────

/**
 * KPIs agrégés sur la période + valeurs de la période précédente.
 */
export function useStatsSummary(
  appId: string | null,
  from: string,
  to: string,
) {
  return useQuery<StatsSummary, Error>({
    queryKey: statsKeys.summary(appId ?? '', from, to),
    queryFn: () =>
      api.get<StatsSummary>(
        `/api/stats/summary?appId=${appId ?? ''}&from=${from}&to=${to}`,
      ),
    enabled: Boolean(appId) && Boolean(from) && Boolean(to),
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Série temporelle journalière d'une métrique.
 */
export function useStatsSeries(
  appId: string | null,
  metric: string,
  from: string,
  to: string,
) {
  return useQuery<{ points: { date: string; value: number }[] }, Error>({
    queryKey: statsKeys.series(appId ?? '', metric, from, to),
    queryFn: () =>
      api.get<{ points: { date: string; value: number }[] }>(
        `/api/stats/series?appId=${appId ?? ''}&metric=${encodeURIComponent(metric)}&from=${from}&to=${to}`,
      ),
    enabled: Boolean(appId) && Boolean(metric) && Boolean(from) && Boolean(to),
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Derniers avis (App Store).
 */
export function useReviews(appId: string | null, limit = 20) {
  return useQuery<AppReview[], Error>({
    queryKey: statsKeys.reviews(appId ?? '', limit),
    queryFn: () =>
      api
        .get<{ reviews: AppReview[] }>(
          `/api/stats/reviews?appId=${appId ?? ''}&limit=${limit}`,
        )
        .then((r) => r.reviews),
    enabled: Boolean(appId),
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Cibles + valeur courante.
 */
export function useTargets(appId: string | null) {
  return useQuery<(StatsTarget & { current: number })[], Error>({
    queryKey: statsKeys.targets(appId ?? ''),
    queryFn: () =>
      api
        .get<{ targets: (StatsTarget & { current: number })[] }>(
          `/api/stats/targets?appId=${appId ?? ''}`,
        )
        .then((r) => r.targets),
    enabled: Boolean(appId),
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Dernier statut de synchro par source.
 */
export function useSync(appId: string | null) {
  return useQuery<StatsSync[], Error>({
    queryKey: statsKeys.sync(appId ?? ''),
    queryFn: () =>
      api
        .get<{ syncs: StatsSync[] }>(`/api/stats/sync?appId=${appId ?? ''}`)
        .then((r) => r.syncs),
    enabled: Boolean(appId),
    staleTime: 1000 * 60 * 2,
  })
}

/**
 * Déclenche une collecte à la demande.
 * Invalide summary, series, reviews, targets, sync après succès.
 */
export function useRefreshStats(appId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<
    { results: { source: string; status: string; message: string | null }[] },
    Error,
    void
  >({
    mutationFn: () =>
      api.post<{
        results: { source: string; status: string; message: string | null }[]
      }>(`/api/stats/refresh?appId=${appId ?? ''}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

/**
 * Intégrations (external_id + enabled par source).
 */
export function useAppIntegrations(appId: string | null) {
  return useQuery<AppIntegration[], Error>({
    queryKey: statsKeys.integrations(appId ?? ''),
    queryFn: () =>
      api
        .get<{ integrations: AppIntegration[] }>(
          `/api/app-integrations?appId=${appId ?? ''}`,
        )
        .then((r) => r.integrations),
    enabled: Boolean(appId),
    staleTime: 1000 * 60 * 5,
  })
}

export type UpsertIntegrationInput = {
  appId: string
  source: 'revenuecat' | 'app_store_connect'
  externalId: string | null
  enabled: boolean
}

/**
 * Upsert d'une intégration (external_id + enabled).
 */
export function useUpsertAppIntegration(appId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<AppIntegration, Error, UpsertIntegrationInput>({
    mutationFn: ({ appId: mutAppId, ...rest }) =>
      api
        .put<{ integration: AppIntegration }>(
          `/api/app-integrations?appId=${encodeURIComponent(mutAppId)}`,
          rest,
        )
        .then((r) => r.integration),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: statsKeys.integrations(appId ?? ''),
      })
    },
  })
}
